import collectiveApi from 'modules/common/collectiveApi';

export type ReferralShareLinkResponse = {
    share_link: string | null;
    referral_code: string | null;
};

function pickNonEmptyString(...values: unknown[]): string | null {
    const trimmed = values
        .filter((v) => v != null && v !== '')
        .map((v) => String(v).trim())
        .find((s) => s.length > 0);
    return trimmed ?? null;
}

/**
 * Impact-style payload: `share_link` may be a string or an object with `cleanShareLink`
 * and channel blocks (`UNKNOWN`, `EMAIL`, `MOBILE`, …) each with `DIRECT` URL.
 */
export function extractShareLinkFromPayloadValue(shareLink: unknown): string | null {
    if (shareLink == null) {
        return null;
    }
    if (typeof shareLink === 'string') {
        return pickNonEmptyString(shareLink);
    }
    if (typeof shareLink !== 'object') {
        return null;
    }
    const o = shareLink as Record<string, unknown>;
    const clean = pickNonEmptyString(o.cleanShareLink, o.clean_share_link);
    if (clean) {
        return clean;
    }
    const urlFromChannel = Object.keys(o)
        .filter((key) => key !== 'cleanShareLink' && key !== 'clean_share_link')
        .map((key) => o[key])
        .filter((channel): channel is Record<string, unknown> => !!channel && typeof channel === 'object')
        .map((channel) => pickNonEmptyString(channel.DIRECT))
        .find((url): url is string => url != null);

    return urlFromChannel ?? null;
}

/**
 * Normalizes GET /referral-share-link/ JSON — backends may use snake_case, camelCase,
 * or alternate keys (e.g. referral_code_for_sharing).
 */
export function normalizeReferralShareLinkResponse(data: unknown): ReferralShareLinkResponse {
    if (!data || typeof data !== 'object') {
        return { share_link: null, referral_code: null };
    }
    const r = data as Record<string, unknown>;

    const rawShare = r.share_link ?? r.shareLink;
    const fromNestedOrString =
        extractShareLinkFromPayloadValue(rawShare) ??
        (typeof rawShare === 'string' ? pickNonEmptyString(rawShare) : null);

    const shareLink =
        fromNestedOrString ?? pickNonEmptyString(r.share_url, r.shareUrl, r.program_share_link, r.programShareLink);

    const referralCode = pickNonEmptyString(
        r.referral_code,
        r.referralCode,
        r.referral_code_for_sharing,
        r.referralCodeForSharing
    );

    return { share_link: shareLink, referral_code: referralCode };
}

/**
 * Backend: GET /referral-share-link/ — Impact share URL and/or referral code for sharing.
 * Uses collectiveApi (auth + base URL) like other member endpoints.
 */
export async function fetchReferralShareLink(): Promise<ReferralShareLinkResponse> {
    const { data } = await collectiveApi.get<unknown>('/referral-share-link/');
    return normalizeReferralShareLinkResponse(data);
}

/** Prefer Impact program URL; otherwise build Collective share URL from API referral code. */
export function getReferralCopyUrl(response: ReferralShareLinkResponse): string | null {
    if (response.share_link) {
        return response.share_link;
    }
    if (response.referral_code) {
        return `https://share.collective.com/${response.referral_code}`;
    }
    return null;
}
